import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/SectionHeading";
import { VimeoLite } from "@/components/VimeoLite";
import { useMeta } from "@/hooks/use-meta";
import { useBreadcrumbSchema } from "@/hooks/use-breadcrumb-schema";

const videos = [
  {
    title: "Handover for Admin Users",
    description:
      "Learn how to use the JobCam app as an Admin user and walk through the 3 schedules you can set up for your JobCam cameras.",
    vimeoId: "771842626",
    poster: "/handover-admin-users-poster.webp",
    duration: "07:07",
  },
  {
    title: "How to Retrieve Footage from the JobCam App",
    description:
      "Export video clips from the JobCam app on your mobile device so you can forward events to Police, management, or your insurance company.",
    vimeoId: "649827300",
    poster: "/retrieve-footage-poster.webp",
    duration: "01:05",
  },
  {
    title: "How to Create a Time-Lapse Video from Your JobCams",
    description:
      "Step-by-step instructions for creating and downloading a Time-Lapse video directly from your JobCam cameras.",
    vimeoId: "706814724",
    poster: "/create-time-lapse-poster.webp",
    duration: "03:18",
  },
  {
    title: "How to Arm the Siren and Strobe from the JobCam App",
    description:
      "Turn the Siren and Strobe on or off, and schedule them to activate automatically at the times that suit your site.",
    vimeoId: "689586326",
    poster: "/arm-siren-strobe-poster.webp",
    duration: "04:28",
  },
  {
    title: "How to Use the 2-Way Audio Feature in the JobCam App",
    description:
      "Speak to someone on site or transmit a warning message directly through your JobCam cameras using the 2-Way Audio feature in the app.",
    vimeoId: "690341185",
    poster: "/two-way-audio-poster.webp",
    duration: "02:04",
  },
  {
    title: "Set Up Reliable Alerts Using Analytics in the JobCam App",
    description:
      "Stop false alerts at night. Use Analytics to trigger notifications only for the events that matter — like a person or a vehicle entering your site.",
    vimeoId: "650214586",
    poster: "/analytics-alerts-poster.webp",
    duration: "04:44",
  },
];

export default function Support() {
  useMeta({
    title: "Support — How-To Videos for the JobCam App",
    description:
      "Need a hand? Watch step-by-step JobCam how-to videos covering admin handover, retrieving footage, time-lapse, siren and strobe, and more.",
  });

  useBreadcrumbSchema([
    { name: "Home", url: "https://jobcam.com.au/" },
    { name: "Support", url: "https://jobcam.com.au/support" },
  ]);

  return (
    <>
      {/* Hero */}
      <section className="relative gradient-hero py-20 md:py-28 overflow-hidden">
        <div className="container relative z-10 text-center max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary text-primary text-xs font-semibold uppercase tracking-widest mb-6"
          >
            <LifeBuoy className="h-3.5 w-3.5" /> Support Centre
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-6xl font-bold text-foreground mb-6"
          >
            Need a Hand? <span className="text-gradient-orange">Watch Our How-To Videos</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground text-lg leading-relaxed mb-8"
          >
            We answer the most common questions about your JobCam CCTV system and show you how to
            resolve issues step by step. Share these videos with your team — rather than running
            training sessions, point your crew at our short, easy-to-follow videos covering the
            most frequently asked questions.
          </motion.p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:support@jobcam.com.au">
              <Button variant="hero" size="lg">
                <Mail className="h-4 w-4" /> Email Support
              </Button>
            </a>
            <a href="tel:1800562226">
              <Button variant="hero-outline" size="lg">
                <Phone className="h-4 w-4" /> 1800 JOBCAM
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Videos */}
      <section className="py-20 bg-background">
        <div className="container">
          <SectionHeading
            label="How-To Library"
            title="Step-by-Step Video Guides"
            description="We're adding to this collection regularly. If there's something we've missed, email support@jobcam.com.au and we'll cover it."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {videos.map((video, idx) => (
              <motion.article
                key={video.vimeoId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card rounded-xl overflow-hidden shadow-card flex flex-col"
              >
                <div className="aspect-video w-full bg-navy-deep">
                  <VimeoLite vimeoId={video.vimeoId} poster={video.poster} alt={video.title} />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="font-display text-xl md:text-2xl font-semibold text-foreground">
                      {video.title}
                    </h2>
                    <span className="shrink-0 text-xs font-semibold text-primary bg-secondary px-2.5 py-1 rounded-full">
                      {video.duration}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {video.description}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-secondary/30">
        <div className="container max-w-3xl text-center">
          <SectionHeading
            label="Still Stuck?"
            title="Talk to a Real Human"
            description="Our Australian-based support team is here to help — whether it's an account question, a hardware issue, or anything in between."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <a
              href="mailto:support@jobcam.com.au"
              className="glass-card rounded-xl p-6 hover:border-primary/50 transition-colors group"
            >
              <Mail className="h-8 w-8 text-primary mb-3 mx-auto" />
              <h3 className="font-display text-lg font-semibold text-foreground mb-1">Email</h3>
              <p className="text-muted-foreground text-sm group-hover:text-primary transition-colors">
                support@jobcam.com.au
              </p>
            </a>
            <a
              href="tel:1800562226"
              className="glass-card rounded-xl p-6 hover:border-primary/50 transition-colors group"
            >
              <Phone className="h-8 w-8 text-primary mb-3 mx-auto" />
              <h3 className="font-display text-lg font-semibold text-foreground mb-1">Phone</h3>
              <p className="text-muted-foreground text-sm group-hover:text-primary transition-colors">
                1800 JOBCAM (562 226)
              </p>
            </a>
          </div>
          <div className="mt-10">
            <Link to="/contact">
              <Button variant="hero" size="lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
